// Widget rendering for AI Chat Dashboards.
//
// Locked decision (ai-chat-dashboard-tracker.md, resolved 4 Aug 2026): the
// agent NEVER emits HTML for a dashboard. It emits a structured widget
// spec (sql + viz_type + viz_config); this component takes the RESULT
// ROWS the backend already ran that SQL into and renders them via real
// Cloudscape components (Table / BarChart / LineChart / a plain stat
// block) — data flows in as component props/values, never as an HTML
// string assembled via interpolation. This is the actual safety
// mechanism (React's own prop/children rendering treats data as plain
// values, not markup) — used identically for both the live-build draft
// preview (DashboardDraftPreview.jsx) and the saved dashboard page
// (DashboardPage.jsx), so there is exactly one rendering code path to
// keep safe, not two.

import React from 'react';
import { Table, BarChart, LineChart, Box, StatusIndicator, Alert, ButtonDropdown } from '@cloudscape-design/components';
import { downloadWidgetDataCsv, downloadWidgetQuery } from './dashboardExport.js';

// Common domain acronyms that should stay upper-case when a raw SQL
// column name (snake_case) is turned into a human-readable header —
// "gms" -> "GMS", not "Gms". Everything else gets plain Title Case.
const HEADER_ACRONYMS = new Set([
  'gms', 'aov', 'asp', 'roi', 'sku', 'fba', 'hsn', 'id', 'ist', 'utc',
  'yoy', 'wow', 'mom', 'dsr', 'rto', 'ndr', 'tpv', 'cod', 'kyc', 'dex', 'ga',
]);

function formatColumnHeader(col) {
  if (!col) return col;
  return col
    .split('_')
    .filter(Boolean)
    .map((word) => {
      const lower = word.toLowerCase();
      return HEADER_ACRONYMS.has(lower) ? lower.toUpperCase() : lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function SingleStatWidget({ columns, rows, vizConfig }) {
  if (!rows || rows.length === 0) {
    return <Box color="text-status-inactive">No data.</Box>;
  }
  const valueColumn = vizConfig?.value_column || columns[0];
  const colIndex = Math.max(columns.indexOf(valueColumn), 0);
  const rawValue = rows[0]?.[colIndex];
  const displayValue = rawValue === null || rawValue === undefined
    ? '—'
    : typeof rawValue === 'number'
      ? rawValue.toLocaleString()
      : String(rawValue);

  // No label rendered under the value — the widget's own title (e.g.
  // "Total GMS — Last 30 Days", shown in ai-chat-widget-header above)
  // already says what this number is. A second "Total GMS" caption here
  // was pure duplication.
  return (
    <div className="ai-chat-widget-single-stat">
      <div className="ai-chat-widget-single-stat-value">{displayValue}</div>
    </div>
  );
}

function TableWidget({ columns, rows }) {
  if (!columns || columns.length === 0) {
    return <Box color="text-status-inactive">No columns returned.</Box>;
  }
  const items = rows.map((row, i) => {
    const item = { __rowKey: i };
    columns.forEach((col, idx) => { item[col] = row[idx]; });
    return item;
  });
  const columnDefinitions = columns.map((col) => ({
    id: col,
    header: formatColumnHeader(col),
    cell: (item) => {
      const v = item[col];
      if (v === null || v === undefined) return '—';
      return typeof v === 'number' ? v.toLocaleString() : String(v);
    },
  }));

  return (
    <Table
      items={items}
      columnDefinitions={columnDefinitions}
      trackBy="__rowKey"
      variant="embedded"
      wrapLines={false}
      empty={<Box textAlign="center" color="inherit">No rows.</Box>}
    />
  );
}

function BarOrLineWidget({ columns, rows, vizConfig, vizType }) {
  if (!rows || rows.length === 0) {
    return <Box color="text-status-inactive">No data.</Box>;
  }
  const xColumn = vizConfig?.x || columns[0];
  const yColumns = (vizConfig?.y && vizConfig.y.length > 0) ? vizConfig.y : columns.slice(1);
  const xIndex = Math.max(columns.indexOf(xColumn), 0);

  const series = yColumns.map((yCol) => {
    const yIndex = columns.indexOf(yCol);
    if (yIndex === -1) return null;
    return {
      title: formatColumnHeader(yCol),
      type: vizType === 'bar' ? 'bar' : 'line',
      data: rows.map((row) => ({ x: row[xIndex], y: typeof row[yIndex] === 'number' ? row[yIndex] : 0 })),
    };
  }).filter(Boolean);

  if (series.length === 0) {
    return <Alert type="warning">Couldn't determine which columns to chart — showing as a table instead.</Alert>;
  }

  // Auto-detect x-axis scale type: if the first x-value is a string (date,
  // label, category name), use "categorical" so Cloudscape renders the
  // actual values as axis labels. Only use "linear" when x-values are
  // genuinely numeric (e.g. a scatter plot with numeric dimensions — rare
  // in this tool's use cases). This fixes the real bug where date-string
  // x-values (e.g. "2026-07-07") rendered as decimals (0, 0.2, 0.4...)
  // because "linear" tried to parse them as numbers and failed.
  const firstX = rows[0]?.[xIndex];
  const xIsNumeric = typeof firstX === 'number';
  const xScaleType = xIsNumeric ? 'linear' : 'categorical';

  const ChartComponent = vizType === 'bar' ? BarChart : LineChart;
  return (
    <ChartComponent
      series={series}
      xScaleType={xScaleType}
      height={220}
      hideFilter
      xTitle={formatColumnHeader(xColumn)}
      yTitle={yColumns.map(formatColumnHeader).join(', ')}
      i18nStrings={{
        filterLabel: 'Filter',
        filterPlaceholder: 'Filter data',
        legendAriaLabel: 'Legend',
        chartAriaRoleDescription: `${vizType} chart`,
      }}
      empty={<Box textAlign="center">No data available</Box>}
      noMatch={<Box textAlign="center">No matching data</Box>}
    />
  );
}

// Row count above which a table widget gets capped to an internally
// scrolling box (screen) / a hard height with a truncation note (print).
// Fixes the real incident where a 500-row table with no cap at all
// blew a dashboard PDF export out to 30+ pages and clipped trailing
// columns. See ai-chat.css's .ai-chat-widget-table-capped for the
// height/scroll rules this class name triggers.
const TABLE_CAP_THRESHOLD = 20;

export default function WidgetRenderer({ widget }) {
  const { title, viz_type: vizType, viz_config: vizConfig, columns, rows, ok, error, row_count } = widget;

  const isKpi = vizType === 'single_stat';
  const isTable = vizType === 'table';
  const isCappedTable = isTable && ok && (rows?.length || 0) > TABLE_CAP_THRESHOLD;

  const widgetClasses = [
    'ai-chat-widget',
    isKpi && 'ai-chat-widget-kpi',
    isCappedTable && 'ai-chat-widget-table-capped',
  ].filter(Boolean).join(' ');

  return (
    <div className={widgetClasses}>
      <div className="ai-chat-widget-header">
        <span className="ai-chat-widget-title">{title}</span>
        <span className="ai-chat-widget-header-actions">
          {ok && typeof row_count === 'number' && vizType !== 'single_stat' && (
            <span className="ai-chat-widget-row-count">{row_count} row{row_count === 1 ? '' : 's'}</span>
          )}
          {/* Per-widget download — deliberately plain CSV (data) + plain
              .sql (query) rather than one multi-sheet workbook: a CSV
              file has no concept of tabs/sheets at all, so "N widgets ->
              1 file with 2N sheets" is only representable as an .xlsx,
              which would have pulled in a new dependency for something
              this simple. Two small files per widget, on demand, is
              simpler code and lets a user grab just the one widget they
              care about instead of always getting everything. */}
          <ButtonDropdown
            items={[
              { id: 'data', text: 'Download data (.csv)', disabled: !ok },
              { id: 'query', text: 'Download query (.sql)' },
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'data') downloadWidgetDataCsv(widget);
              if (detail.id === 'query') downloadWidgetQuery(widget);
            }}
            variant="icon"
            ariaLabel={`Download options for ${title}`}
          />
        </span>
      </div>
      <div className="ai-chat-widget-body">
        {!ok ? (
          <StatusIndicator type="error">{error || 'Query failed.'}</StatusIndicator>
        ) : vizType === 'single_stat' ? (
          <SingleStatWidget columns={columns} rows={rows} vizConfig={vizConfig} />
        ) : vizType === 'bar' || vizType === 'line' ? (
          <BarOrLineWidget columns={columns} rows={rows} vizConfig={vizConfig} vizType={vizType} />
        ) : (
          <TableWidget columns={columns} rows={rows} />
        )}
      </div>
      {isCappedTable && (
        <div className="ai-chat-widget-table-note">
          Showing all {row_count} rows — scroll within the table to see more.
        </div>
      )}
    </div>
  );
}
